import { useState } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { PaymentProcessor, PaymentProcessorUsageFunction } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (processor: PaymentProcessor) => void;
}

const USAGE_TEMPLATES: PaymentProcessorUsageFunction[] = [
  { id: 'new_radio', label: 'Nuevas radios', enabled: false },
  { id: 'renewal', label: 'Renovaciones', enabled: false },
  { id: 'annual_license', label: 'Licencia anual', enabled: false },
  { id: 'preferred_frequency', label: 'Frecuencia preferida', enabled: false },
  { id: 'premium_domain', label: 'Dominio premium', enabled: false },
  { id: 'plan_upgrade', label: 'Upgrade plan', enabled: false },
  { id: 'marketplace', label: 'Marketplace futuro', enabled: false },
];

const CATEGORY_OPTIONS = [
  { value: 'api', label: 'API', icon: 'ri-plug-line', color: 'bg-sky-50 text-sky-600' },
  { value: 'manual', label: 'Manual', icon: 'ri-file-list-3-line', color: 'bg-amber-50 text-amber-600' },
] as const;

function freshId(): string {
  return `proc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function CreateProcessorModal({ open, onClose, onCreate }: Props) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'api' | 'manual'>('api');
  const [icon, setIcon] = useState('ri-bank-card-line');
  const [logoColor, setLogoColor] = useState('bg-violet-50 text-violet-600');
  const [commissionPercent, setCommissionPercent] = useState(0);
  const [commissionFixed, setCommissionFixed] = useState(0);
  const [usageFunctions, setUsageFunctions] = useState<PaymentProcessorUsageFunction[]>(
    USAGE_TEMPLATES.map((f) => ({ ...f })),
  );
  const [saving, setSaving] = useState(false);

  const handleToggleUsage = (fnId: string) => {
    setUsageFunctions((prev) =>
      prev.map((f) => (f.id === fnId ? { ...f, enabled: !f.enabled } : f)),
    );
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      SwalAlert.warning('Nombre requerido', 'El nombre del procesador es obligatorio.');
      return;
    }
    if (!icon.trim()) {
      SwalAlert.warning('Icono requerido', 'Seleccioná un ícono de Remix Icon.');
      return;
    }

    setSaving(true);
    await new Promise((r) => setTimeout(r, 500));
    setSaving(false);

    const newProcessor: PaymentProcessor = {
      id: freshId(),
      name: trimmed,
      icon: icon.trim(),
      logoColor: logoColor.trim() || 'bg-foreground-50 text-foreground-600',
      category,
      status: 'inactive',
      enabled: false,
      isDefault: false,
      testMode: category === 'api',
      order: 99,
      lastSync: null,
      lastTest: null,
      webhookStatus: category === 'api' ? 'unconfigured' : null,
      commissionPercent,
      commissionFixed,
      descriptor: '',
      config: category === 'api'
        ? [{ key: 'api_key', label: 'API Key', value: '', type: 'text' as const, placeholder: 'sk_...', help: 'Clave de API del proveedor' }]
        : [{ key: 'instructions', label: 'Instrucciones', value: '', type: 'textarea' as const, placeholder: 'Instrucciones para el pagador...' }],
      permissions: [
        { label: 'Procesar pagos', enabled: true },
        { label: 'Procesar reembolsos', enabled: category === 'api' },
      ],
      usageFunctions,
    };

    onCreate(newProcessor);
    SwalAlert.toast(`${trimmed} creado como borrador`, 'success');
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setName('');
    setCategory('api');
    setIcon('ri-bank-card-line');
    setLogoColor('bg-violet-50 text-violet-600');
    setCommissionPercent(0);
    setCommissionFixed(0);
    setUsageFunctions(USAGE_TEMPLATES.map((f) => ({ ...f })));
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const enabledUsageCount = usageFunctions.filter((f) => f.enabled).length;

  return (
    <ModalBase
      open={open}
      onClose={handleCancel}
      title="Nuevo Procesador"
      subtitle="Configurá un nuevo método de pago para la plataforma"
      size="lg"
      headerIcon={
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
          <i className="ri-add-circle-line text-accent-500 text-sm"></i>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-foreground-700 text-[10px] font-semibold mb-1">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
              placeholder="Ej: Mercado Pago"
            />
          </div>

          <div>
            <label className="block text-foreground-700 text-[10px] font-semibold mb-1">Ícono (Remix Icon)</label>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
              placeholder="ri-bank-card-line"
            />
          </div>

          <div>
            <label className="block text-foreground-700 text-[10px] font-semibold mb-1">Color (Tailwind)</label>
            <input
              type="text"
              value={logoColor}
              onChange={(e) => setLogoColor(e.target.value)}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
              placeholder="bg-violet-50 text-violet-600"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-foreground-700 text-[10px] font-semibold mb-2">Categoría</label>
          <div className="inline-flex rounded-xl bg-background-100 p-1">
            {CATEGORY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setCategory(opt.value)}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${category === opt.value ? 'bg-foreground-950 text-background-50' : 'text-foreground-400 hover:text-foreground-600'}`}
              >
                <i className={`${opt.icon} text-[11px]`}></i>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Commission */}
        <div>
          <label className="block text-foreground-700 text-[10px] font-semibold mb-2">Comisión por transacción</label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">Porcentaje (%)</label>
              <div className="relative">
                <input
                  type="number"
                  value={commissionPercent}
                  onChange={(e) => setCommissionPercent(parseFloat(e.target.value) || 0)}
                  step="0.01" min="0"
                  className="w-full px-3 py-2 pr-10 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">%</span>
              </div>
            </div>
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">Fijo (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-bold">$</span>
                <input
                  type="number"
                  value={commissionFixed}
                  onChange={(e) => setCommissionFixed(parseFloat(e.target.value) || 0)}
                  step="0.01" min="0"
                  className="w-full pl-7 pr-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Usage functions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-foreground-700 text-[10px] font-semibold">Dónde se va a usar</label>
            <span className="text-foreground-400 text-[9px]">{enabledUsageCount}/{usageFunctions.length}</span>
          </div>
          <div className="bg-background-100 rounded-xl overflow-hidden divide-y divide-background-200/70">
            {usageFunctions.map((fn) => (
              <div key={fn.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-background-50 transition-colors">
                <span className={`text-xs truncate ${fn.enabled ? 'text-foreground-700 font-medium' : 'text-foreground-400'}`}>
                  {fn.label}
                </span>
                <button
                  onClick={() => handleToggleUsage(fn.id)}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${fn.enabled ? 'bg-emerald-500' : 'bg-background-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${fn.enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ModalBase
        open={false}
        onClose={() => {}}
        title=""
        footer={
          <>
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreate}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-1.5"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xs"></i>Creando...
                </>
              ) : (
                <>
                  <i className="ri-add-line text-[11px]"></i>Crear Procesador
                </>
              )}
            </button>
          </>
        }
      />
    </ModalBase>
  );
}